// Authored plan-design models, validation, compilation, and group resolution.
//
// The authored objects in this module are the only persisted plan source:
//
//   plan.employerContributions.profitSharing — discriminated PS union (§2.3)
//   plan.cashBalance                   — structured benefit-credit design (§2.4)
//
// `compileToEngine` derives the calculation-engine shape (profitSharingAllocations
// components and an unwrapped cash-balance projection) EPHEMERALLY for
// calculation and export calls. It is plan-only: it never reads employees,
// local assignments, or solver target IDs — those enter through the runtime
// resolution steps in calculations.js (§2.5b).
//
// Import direction: this module depends only on constants. planSettings.js
// builds plan-level normalization on top.

import {
  CASH_BALANCE_SUPPORTED_PLAN_YEAR,
  DEFAULT_CASH_BALANCE,
  DEFAULT_MATCH_TIERS,
} from './constants.js'

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value)
const deepClone = (value) => JSON.parse(JSON.stringify(value))

// =============================================================================
// Allocation classes (§2.2): a class code lives on the CENSUS ROW and the plan
// stores one formula per class. The class list is derived from the census, not
// authored here — see allocationClasses.js for the model and why membership is
// never computed from HCE status.
// =============================================================================

import {
  EVERYONE_ELSE_GROUP_ID,
  classLabelById,
  resolveClassMembership,
} from './allocationClasses.js'

export {
  EVERYONE_ELSE_GROUP_ID,
  EVERYONE_ELSE_LABEL,
  slugifyGroupId,
} from './allocationClasses.js'

// Every class a plan's rates still point at, with the label each rate carried
// for itself. Unioned into the derived class list so loading a plan against a
// census that lacks one of its classes surfaces the rate instead of dropping it.
export function referencedClassesForPlan(plan) {
  const seen = new Map()
  const add = (groupId, groupLabel) => {
    const id = String(groupId || '').trim()
    if (!id || seen.has(id)) return
    seen.set(id, { id, label: String(groupLabel || '').trim() || id })
  }
  const design = selectProfitSharingDesign(plan)
  if (design?.formula?.type === 'new_comparability') {
    for (const entry of design.formula.groups || []) add(entry?.groupId, entry?.groupLabel)
  }
  for (const entry of plan?.employerContributions?.matchGroups || []) add(entry?.groupId, entry?.groupLabel)
  for (const entry of plan?.cashBalance?.credits || []) add(entry?.groupId, entry?.groupLabel)
  return [...seen.values()]
}

// Legacy plan files and stored bundles carry an authored `allocationGroups`
// array. It is no longer part of the plan contract: the class list comes from
// the census. This reads such an array only so migration can recover the
// id → label mapping needed to write class codes back onto census rows.
export function legacyGroupLabelsById(value) {
  const labels = {}
  for (const entry of Array.isArray(value) ? value : []) {
    if (!isObject(entry)) continue
    const id = String(entry.id || '').trim()
    const label = String(entry.label || '').trim()
    if (id && label && id !== EVERYONE_ELSE_GROUP_ID) labels[id] = label
  }
  return labels
}

// =============================================================================
// Calculation-only profit-sharing components. The authored standard union
// compiles down to these objects; they are never accepted as plan authoring.
// (Moved here from planSettings.js so compilation and normalization share one
// definition without an import cycle.)
// =============================================================================

export const PS_COMPONENT_TYPES = ['simple', 'group_classes', 'per_person', 'age_weighted', 'integrated', 'points', 'comp_pool']

export const normalizeProfitSharingAllocation = (value = {}, index = 0) => {
  const type = PS_COMPONENT_TYPES.includes(value.type) ? value.type : 'simple'
  const id = String(value.id || `profit-sharing-${type}-${index + 1}`).trim()
  if (type === 'integrated') {
    // Shape hygiene only. Band legality (the §401(l) disparity ladder and the
    // lesser-of rule) is engine validation that REJECTS with requires-review —
    // the normalizer must never silently clamp a disparity into legality.
    const levelSource = isObject(value.integrationLevel) ? value.integrationLevel : {}
    const levelType = ['twb', 'custom_percent_of_twb', 'custom_dollar'].includes(levelSource.type)
      ? levelSource.type
      : 'twb'
    return {
      id,
      type,
      basePercent: isFiniteNumber(value.basePercent) ? Math.max(0, value.basePercent) : 0,
      disparityPercent: isFiniteNumber(value.disparityPercent) ? Math.max(0, value.disparityPercent) : 0,
      integrationLevel: levelType === 'twb'
        ? { type: 'twb' }
        : { type: levelType, value: isFiniteNumber(levelSource.value) ? Math.max(0, levelSource.value) : 0 },
    }
  }
  if (type === 'points') {
    return {
      id,
      type,
      perYearOfAge: isFiniteNumber(value.perYearOfAge) ? Math.max(0, value.perYearOfAge) : 0,
      perYearOfService: isFiniteNumber(value.perYearOfService) ? Math.max(0, value.perYearOfService) : 0,
      perCompUnit: isFiniteNumber(value.perCompUnit) ? Math.max(0, value.perCompUnit) : 0,
      compUnit: isFiniteNumber(value.compUnit) && value.compUnit > 0 ? value.compUnit : 100,
      contributionPool: isFiniteNumber(value.contributionPool) ? Math.max(0, value.contributionPool) : 0,
    }
  }
  if (type === 'comp_pool') {
    return {
      id,
      type,
      contributionPool: isFiniteNumber(value.contributionPool) ? Math.max(0, value.contributionPool) : 0,
    }
  }
  if (type === 'group_classes') {
    return {
      id,
      type,
      groups: Array.isArray(value.groups) ? value.groups.map((group, groupIndex) => ({
        groupId: String(group?.groupId || `group-${groupIndex + 1}`).trim(),
        formula: {
          method: ['percent', 'flat', 'fill_415c'].includes(group?.formula?.method) ? group.formula.method : 'percent',
          percent: isFiniteNumber(group?.formula?.percent) ? Math.max(0, group.formula.percent) : 0,
          amount: isFiniteNumber(group?.formula?.amount) ? Math.max(0, group.formula.amount) : 0,
        },
      })) : [],
    }
  }
  if (type === 'per_person') {
    return { id, type, source: 'profitSharing' }
  }
  if (type === 'age_weighted') {
    return {
      id,
      type,
      normalRetirementAge: isFiniteNumber(value.normalRetirementAge)
        ? Math.min(100, Math.max(40, value.normalRetirementAge)) : 65,
      assumptionSet: String(value.assumptionSet || 'retira-standard-2026-v1'),
      excessHandling: value.excessHandling === 'unallocated' ? 'unallocated' : 'requires-review',
      contributionPool: isFiniteNumber(value.contributionPool) ? Math.max(0, value.contributionPool) : 0,
    }
  }
  const method = ['percent', 'flat'].includes(value.method) ? value.method : 'percent'
  return {
    id,
    type,
    method,
    percent: isFiniteNumber(value.percent) ? Math.max(0, value.percent) : 0,
    amount: isFiniteNumber(value.amount) ? Math.max(0, value.amount) : 0,
  }
}

// =============================================================================
// Authored profit-sharing union (§2.3)
// =============================================================================

export const PS_STANDARD_FORMULA_TYPES = ['pro_rata', 'flat_dollar', 'integrated', 'points', 'age_weighted', 'new_comparability']

// formula → allowed funding modes (§2.3 legality matrix)
export const PS_FUNDING_MATRIX = Object.freeze({
  pro_rata: ['rate', 'budget'],
  flat_dollar: ['rate'],
  integrated: ['rate'],
  points: ['budget'],
  age_weighted: ['budget'],
  new_comparability: ['rate', 'maximize'],
})

const normalizeConditions = (value = {}) => ({
  minHours: isFiniteNumber(value.minHours) ? Math.max(0, Math.round(value.minHours)) : 0,
  lastDayRequired: value.lastDayRequired === true,
  combinator: 'and',
  waivers: null,
})

const normalizeFunding = (value = {}) => {
  if (value.mode === 'budget') {
    return { mode: 'budget', amount: isFiniteNumber(value.amount) ? Math.max(0, value.amount) : 0 }
  }
  if (value.mode === 'maximize') {
    const targets = isObject(value.targets) && value.targets.local === true
      ? { local: true }
      : { rule: 'all-owners' }
    return { mode: 'maximize', targets }
  }
  return { mode: 'rate' }
}

const normalizeStandardFormula = (value = {}, funding) => {
  const type = PS_STANDARD_FORMULA_TYPES.includes(value.type) ? value.type : 'pro_rata'
  if (type === 'pro_rata') {
    // percent belongs to rate funding only; a budget-funded pro rata carries
    // its dollars on funding.amount and omits the stale percent (§2.3).
    return funding.mode === 'rate'
      ? { type, percent: isFiniteNumber(value.percent) ? Math.max(0, value.percent) : 0 }
      : { type }
  }
  if (type === 'flat_dollar') {
    return { type, amount: isFiniteNumber(value.amount) ? Math.max(0, value.amount) : 0 }
  }
  if (type === 'integrated') {
    const levelSource = isObject(value.integrationLevel) ? value.integrationLevel : {}
    const levelType = ['twb', 'custom_percent_of_twb', 'custom_dollar'].includes(levelSource.type)
      ? levelSource.type
      : 'twb'
    return {
      type,
      basePercent: isFiniteNumber(value.basePercent) ? Math.max(0, value.basePercent) : 0,
      disparityPercent: isFiniteNumber(value.disparityPercent) ? Math.max(0, value.disparityPercent) : 0,
      integrationLevel: levelType === 'twb'
        ? { type: 'twb' }
        : { type: levelType, value: isFiniteNumber(levelSource.value) ? Math.max(0, levelSource.value) : 0 },
    }
  }
  if (type === 'points') {
    return {
      type,
      perYearOfAge: isFiniteNumber(value.perYearOfAge) ? Math.max(0, value.perYearOfAge) : 0,
      perYearOfService: isFiniteNumber(value.perYearOfService) ? Math.max(0, value.perYearOfService) : 0,
      perCompUnit: isFiniteNumber(value.perCompUnit) ? Math.max(0, value.perCompUnit) : 0,
      compUnit: isFiniteNumber(value.compUnit) && value.compUnit > 0 ? value.compUnit : 100,
    }
  }
  if (type === 'age_weighted') {
    return {
      type,
      assumptionSet: String(value.assumptionSet || 'retira-standard-2026-v1'),
      normalRetirementAge: isFiniteNumber(value.normalRetirementAge)
        ? Math.min(100, Math.max(40, value.normalRetirementAge)) : 65,
    }
  }
  // new_comparability
  if (value.groupMode === 'per_person') {
    return { type, groupMode: 'per_person', amountMethod: 'flat' }
  }
  return {
    type,
    groupMode: 'grouped',
    // groupLabel rides with the rate so a class stays readable when the loaded
    // census has nobody in it. Dropping it here would leave the editor showing
    // raw slugs next to real class names.
    groups: Array.isArray(value.groups) ? value.groups.map((group, index) => ({
      groupId: String(group?.groupId || `group-${index + 1}`).trim(),
      groupLabel: String(group?.groupLabel || '').trim(),
      formula: {
        method: ['percent', 'flat', 'fill_415c'].includes(group?.formula?.method) ? group.formula.method : 'percent',
        ...(group?.formula?.method === 'fill_415c'
          ? {}
          : { value: isFiniteNumber(group?.formula?.value) ? Math.max(0, group.formula.value) : 0 }),
      },
    })) : [],
  }
}

export function createDefaultProfitSharingDesign() {
  // Standard, disabled, rate-funded pro rata at 0% with no conditions.
  return {
    mode: 'standard',
    enabled: false,
    funding: { mode: 'rate' },
    formula: { type: 'pro_rata', percent: 0 },
    conditions: normalizeConditions(),
  }
}

export function normalizeProfitSharingDesign(value) {
  if (!isObject(value)) return createDefaultProfitSharingDesign()
  const funding = normalizeFunding(value.funding)
  return {
    mode: 'standard',
    enabled: value.enabled === true,
    funding,
    formula: normalizeStandardFormula(isObject(value.formula) ? value.formula : {}, funding),
    conditions: normalizeConditions(value.conditions),
  }
}

export function validateProfitSharingDesign(profitSharing) {
  const errors = []
  const push = (path, message) => errors.push({ path: `profitSharing.${path}`, message, status: 'requires-review' })
  if (!isObject(profitSharing)) {
    push('', 'Profit sharing design must be an object.')
    return errors
  }
  if (profitSharing.mode !== 'standard') {
    push('mode', 'Profit sharing must use the standard authored design.')
    return errors
  }
  const { funding, formula } = profitSharing
  const allowed = PS_FUNDING_MATRIX[formula?.type]
  if (!allowed) {
    push('formula.type', 'Unknown profit-sharing formula.')
    return errors
  }
  if (!allowed.includes(funding?.mode)) {
    push('funding.mode', `A ${formula.type.replace(/_/g, ' ')} formula cannot use ${funding?.mode} funding (allowed: ${allowed.join(', ')}).`)
  }
  if (funding?.mode === 'budget' && !(isFiniteNumber(funding.amount) && funding.amount >= 0)) {
    push('funding.amount', 'Budget funding needs a non-negative dollar amount.')
  }
  if (funding?.mode === 'maximize' && !(funding.targets?.rule === 'all-owners' || funding.targets?.local === true)) {
    push('funding.targets', 'Maximize funding targets must be all owners or a locally-selected list.')
  }
  if (formula.type === 'pro_rata' && funding?.mode === 'rate' && !isFiniteNumber(formula.percent)) {
    push('formula.percent', 'Rate-funded pro rata requires a percent of compensation.')
  }
  if (formula.type === 'pro_rata' && funding?.mode === 'budget' && formula.percent !== undefined) {
    push('formula.percent', 'Budget-funded pro rata carries its dollars on the funding amount; percent must be omitted.')
  }
  if (formula.type === 'flat_dollar' && !isFiniteNumber(formula.amount)) {
    push('formula.amount', 'Flat dollar requires an amount per eligible employee.')
  }
  if (formula.type === 'new_comparability' && formula.groupMode === 'grouped') {
    if (!Array.isArray(formula.groups) || formula.groups.length === 0) {
      push('formula.groups', 'New comparability needs at least one class rate.')
    } else {
      // A rate pointing at a class nobody in the census carries is a review
      // FINDING (collectGroupReviewFindings), not a validation error: the class
      // list is derived from the census, so a plan loaded against a different
      // census would otherwise be invalid rather than merely incomplete.
      const ids = formula.groups.map((group) => group.groupId)
      if (new Set(ids).size !== ids.length) {
        push('formula.groups', 'Each allocation group may have at most one formula.')
      }
    }
  }
  return errors
}

// =============================================================================
// Authored cash-balance design (§2.4)
// =============================================================================

export const CB_CREDIT_METHODS = Object.freeze([
  'percentOfPay',
  'flatDollar',
  'serviceGraded',
  'permittedDisparity',
])

const normalizeCashBalanceCreditFormula = (value = {}) => {
  const method = CB_CREDIT_METHODS.includes(value.method) ? value.method : 'percentOfPay'
  if (method === 'serviceGraded') {
    const bands = Array.isArray(value.bands) ? value.bands.map((band, index) => ({
      minYears: isFiniteNumber(band?.minYears) ? Math.max(0, Math.floor(band.minYears)) : index * 5,
      maxYears: isFiniteNumber(band?.maxYears) ? Math.max(0, Math.floor(band.maxYears)) : null,
      percentOfPay: isFiniteNumber(band?.percentOfPay) ? Math.min(100, Math.max(0, band.percentOfPay)) : 0,
      flatDollar: isFiniteNumber(band?.flatDollar) ? Math.max(0, band.flatDollar) : 0,
    })).sort((a, b) => a.minYears - b.minYears) : []
    return { method, bands }
  }
  if (method === 'permittedDisparity') {
    return {
      method,
      basePercent: isFiniteNumber(value.basePercent) ? Math.min(100, Math.max(0, value.basePercent)) : 0,
      excessPercent: isFiniteNumber(value.excessPercent) ? Math.min(100, Math.max(0, value.excessPercent)) : 0,
      integrationLevel: isFiniteNumber(value.integrationLevel) ? Math.max(0, value.integrationLevel) : null,
    }
  }
  return {
    method,
    value: isFiniteNumber(value.value) ? Math.max(0, value.value) : 0,
  }
}

const normalizeCashBalanceInterestCredit = (value = {}) => {
  if (value.type === 'index') {
    const floor = isFiniteNumber(value.floor) ? Math.max(0, value.floor) : 0
    const cap = isFiniteNumber(value.cap) ? Math.max(floor, value.cap) : 6
    return {
      type: 'index',
      index: ['30-year-treasury', '10-year-treasury', 'one-year-treasury', 'custom'].includes(value.index)
        ? value.index : '30-year-treasury',
      spread: isFiniteNumber(value.spread) ? value.spread : 0,
      floor,
      cap,
      assumptionRate: isFiniteNumber(value.assumptionRate)
        ? Math.min(cap, Math.max(floor, value.assumptionRate)) : Math.min(cap, Math.max(floor, 5)),
    }
  }
  return {
    type: 'fixed',
    rate: isFiniteNumber(value.rate) ? Math.min(6, Math.max(0, value.rate)) : 5,
  }
}

const normalizeCashBalanceEligibility = (value = {}) => ({
  enabled: value.enabled === true,
  minAge: isFiniteNumber(value.minAge) ? Math.min(26, Math.max(0, Math.floor(value.minAge))) : 21,
  minServiceMonths: isFiniteNumber(value.minServiceMonths)
    ? Math.min(60, Math.max(0, Math.floor(value.minServiceMonths))) : 12,
  minHours: isFiniteNumber(value.minHours) ? Math.max(0, Math.floor(value.minHours)) : 1000,
  entryFrequency: ['immediate', 'monthly', 'quarterly', 'semiannual', 'annual'].includes(value.entryFrequency)
    ? value.entryFrequency : 'semiannual',
})

const normalizeStructuredCashBalance = (value = {}, planYear) => {
  return {
    mode: 'structured',
    enabled: value.enabled === true && Number(planYear) === CASH_BALANCE_SUPPORTED_PLAN_YEAR,
    // groupLabel is carried alongside the id so a credit stays readable when
    // the loaded census has nobody in that class (the class list is derived
    // from the census, so there is no plan-side registry to look it up in).
    credits: Array.isArray(value.credits) ? value.credits.map((credit, index) => ({
      groupId: String(credit?.groupId || `group-${index + 1}`).trim(),
      groupLabel: String(credit?.groupLabel || '').trim(),
      formula: normalizeCashBalanceCreditFormula(credit?.formula),
    })) : [],
    interestCredit: normalizeCashBalanceInterestCredit(value.interestCredit),
    eligibility: normalizeCashBalanceEligibility(value.eligibility),
  }
}

// Unknown shapes reset to the disabled current design, keeping one editor and
// one calculation contract.
export function normalizeCashBalanceDesign(value, planYear) {
  const source = isObject(value) && value.mode === 'structured' ? value : {}
  return normalizeStructuredCashBalance(source, planYear)
}

export function createDefaultCashBalanceDesign(planYear) {
  return normalizeStructuredCashBalance({}, planYear)
}

// =============================================================================
// Calculation selectors. Readers use these rather than depending on the
// persisted cash-balance shape directly. Compiled engine plans still pass
// through so calculation helpers remain independently testable.
// =============================================================================

export const isAuthoredPlan = (plan) => Boolean(
  isObject(plan)
  && plan?.employerContributions?.profitSharing?.mode === 'standard'
  && plan?.cashBalance?.mode === 'structured',
)

// The engine-shaped cash-balance object. Direct engine fixtures still pass
// through; authored plans always use the structured variant.
export function selectCashBalance(plan) {
  const cashBalance = plan?.cashBalance
  if (!isObject(cashBalance)) return null
  if (cashBalance.mode === 'structured') return projectStructuredCashBalance(cashBalance)
  return cashBalance
}

export const isCashBalanceEnabled = (plan) => selectCashBalance(plan)?.enabled === true

// The authored cash-balance design used by editors and projection surfaces.
export const selectCashBalanceDesign = (plan) => (
  isObject(plan?.cashBalance) ? plan.cashBalance : null
)

export const selectProfitSharingDesign = (plan) => (
  isObject(plan?.employerContributions?.profitSharing) ? plan.employerContributions.profitSharing : null
)

export function isProfitSharingEnabled(plan) {
  const design = selectProfitSharingDesign(plan)
  return design?.enabled === true
}

export function planUsesGroups(plan) {
  if (!plan) return false
  const contributions = plan.employerContributions || {}
  const design = selectProfitSharingDesign(plan)
  const profitSharingUsesGroups = isProfitSharingEnabled(plan)
    && design?.formula?.type === 'new_comparability'
  const matchUsesGroups = contributions.matchEnabled === true && contributions.matchMode === 'grouped'
  const cashBalance = selectCashBalance(plan)
  const cashBalanceUsesGroups = isCashBalanceEnabled(plan) && (cashBalance?.groups || []).length > 0
  return profitSharingUsesGroups || matchUsesGroups || cashBalanceUsesGroups
}

// Which plan features currently read allocation groups, in display order.
// Same predicates as planUsesGroups, kept here so the Groups screen can name
// its consumers instead of asserting a bare "this plan uses groups".
export function describeGroupConsumers(plan) {
  if (!plan) return []
  const contributions = plan.employerContributions || {}
  const design = selectProfitSharingDesign(plan)
  const consumers = []
  if (isProfitSharingEnabled(plan) && design?.formula?.type === 'new_comparability') {
    consumers.push('New-comparability profit sharing')
  }
  if (contributions.matchEnabled === true && contributions.matchMode === 'grouped') {
    consumers.push('Grouped match')
  }
  const cashBalance = selectCashBalance(plan)
  if (isCashBalanceEnabled(plan) && (cashBalance?.groups || []).length > 0) {
    consumers.push('Cash-balance credit groups')
  }
  return consumers
}

// Structured CB → the engine calculation shape. Group-ID credits stay
// keyed by group ID; the runtime resolution step (Phase 3) maps membership.
export function projectStructuredCashBalance(cashBalance) {
  const interestCredit = normalizeCashBalanceInterestCredit(cashBalance.interestCredit)
  return {
    ...deepClone(DEFAULT_CASH_BALANCE),
    enabled: cashBalance.enabled === true,
    keyBy: 'group-id',
    groups: (cashBalance.credits || []).map((credit) => ({
      groupId: credit.groupId,
      groupLabel: credit.groupLabel || '',
      formula: {
        percentOfComp: credit.formula.method === 'percentOfPay' ? credit.formula.value : null,
        flatDollar: credit.formula.method === 'flatDollar' ? credit.formula.value : null,
        method: credit.formula.method,
        serviceBands: credit.formula.method === 'serviceGraded' ? deepClone(credit.formula.bands) : null,
        basePercent: credit.formula.method === 'permittedDisparity' ? credit.formula.basePercent : null,
        excessPercent: credit.formula.method === 'permittedDisparity' ? credit.formula.excessPercent : null,
        integrationLevel: credit.formula.method === 'permittedDisparity' ? credit.formula.integrationLevel : null,
        combine: 'sum',
      },
    })),
    interestCreditRate: interestCredit.type === 'fixed' ? interestCredit.rate : interestCredit.assumptionRate,
    interestCredit,
    eligibility: normalizeCashBalanceEligibility(cashBalance.eligibility),
    vestingSchedule: { type: 'cliff', years: 3 },
  }
}

// =============================================================================
// Compilation: authored plan → ephemeral calculation plan. Plan-only by
// contract.
// =============================================================================

function compileStandardFormula(profitSharing) {
  const { formula, funding } = profitSharing
  const id = 'ps-standard'
  if (formula.type === 'pro_rata') {
    return funding.mode === 'budget'
      ? [{ id, type: 'comp_pool', contributionPool: funding.amount }]
      : [{ id, type: 'simple', method: 'percent', percent: formula.percent ?? 0, amount: 0 }]
  }
  if (formula.type === 'flat_dollar') {
    return [{ id, type: 'simple', method: 'flat', percent: 0, amount: formula.amount ?? 0 }]
  }
  if (formula.type === 'integrated') {
    return [{
      id, type: 'integrated',
      basePercent: formula.basePercent, disparityPercent: formula.disparityPercent,
      integrationLevel: formula.integrationLevel,
    }]
  }
  if (formula.type === 'points') {
    return [{
      id, type: 'points',
      perYearOfAge: formula.perYearOfAge, perYearOfService: formula.perYearOfService,
      perCompUnit: formula.perCompUnit, compUnit: formula.compUnit,
      contributionPool: funding.mode === 'budget' ? funding.amount : 0,
    }]
  }
  if (formula.type === 'age_weighted') {
    return [{
      id, type: 'age_weighted',
      contributionPool: funding.mode === 'budget' ? funding.amount : 0,
      normalRetirementAge: formula.normalRetirementAge,
      assumptionSet: formula.assumptionSet,
      excessHandling: 'unallocated',
    }]
  }
  // new_comparability
  if (formula.groupMode === 'per_person') {
    return [{ id, type: 'per_person', source: 'profitSharing' }]
  }
  return [{
    id, type: 'group_classes',
    groups: (formula.groups || []).map((group) => ({
      groupId: group.groupId,
      formula: {
        method: group.formula.method,
        percent: group.formula.method === 'percent' ? group.formula.value ?? 0 : 0,
        amount: group.formula.method === 'flat' ? group.formula.value ?? 0 : 0,
      },
    })),
  }]
}

export function compileProfitSharingToEngine(profitSharing) {
  const design = normalizeProfitSharingDesign(profitSharing)
  const components = compileStandardFormula(design)
  return {
    profitSharingEnabled: design.enabled,
    profitSharingAllocations: components,
    allocationLimitPolicy: { order: components.map((component) => component.id), excessHandling: 'unallocated' },
    profitSharingConditions: { minHours: design.conditions.minHours, lastDayRequired: design.conditions.lastDayRequired },
  }
}

// =============================================================================
// Calculation-shape normalization. This is one-way and never accepted as an
// authored or persisted plan.
// =============================================================================

export const normalizeMatchTier = (tier = {}) => ({
  matchPercent: isFiniteNumber(tier.matchPercent) ? Math.max(0, tier.matchPercent) : 0,
  upToPercent: isFiniteNumber(tier.upToPercent) ? Math.max(0, tier.upToPercent) : 0,
})

export const normalizeMatchGroup = (value = {}, index = 0) => {
  return {
    id: String(value.id || `match-group-${index + 1}`).trim(),
    groupId: String(value.groupId || '').trim(),
    groupLabel: String(value.groupLabel || '').trim(),
    matchTiers: Array.isArray(value.matchTiers) && value.matchTiers.length
      ? value.matchTiers.map(normalizeMatchTier)
      : deepClone(DEFAULT_MATCH_TIERS),
  }
}

export const normalizeEngineEmployerContributions = (value = {}) => {
  const components = Array.isArray(value.profitSharingAllocations)
    ? value.profitSharingAllocations.map(normalizeProfitSharingAllocation)
    : []
  const matchTiers = Array.isArray(value.matchTiers) && value.matchTiers.length
    ? value.matchTiers.map(normalizeMatchTier)
    : deepClone(DEFAULT_MATCH_TIERS)
  const matchGroups = Array.isArray(value.matchGroups)
    ? value.matchGroups.map(normalizeMatchGroup)
    : []
  const policy = isObject(value.allocationLimitPolicy) ? value.allocationLimitPolicy : {}
  const ids = new Set(components.map((component) => component.id))
  const suppliedOrder = Array.isArray(policy.order) ? policy.order.filter((id) => ids.has(id)) : []
  const completeOrder = suppliedOrder.length === ids.size && new Set(suppliedOrder).size === ids.size
  const allocationLimitPolicy = {
    order: completeOrder ? suppliedOrder : components.length === 1
      ? [components[0].id] : [],
    excessHandling: policy.excessHandling === 'unallocated' ? 'unallocated'
      : components.length === 1 ? 'unallocated' : 'requires-review',
  }
  const conditions = isObject(value.profitSharingConditions) ? value.profitSharingConditions : {}
  return {
    matchEnabled: value.matchEnabled === true,
    matchMode: value.matchMode === 'grouped' ? 'grouped' : 'simple',
    matchTiers,
    matchGroups,
    matchCapPerEmployee: isFiniteNumber(value.matchCapPerEmployee) ? Math.max(0, value.matchCapPerEmployee) : 0,
    necEnabled: value.necEnabled === true,
    necPercent: isFiniteNumber(value.necPercent) ? Math.max(0, value.necPercent) : 0,
    necIsSafeHarbor: value.necIsSafeHarbor === true,
    profitSharingEnabled: value.profitSharingEnabled === true,
    profitSharingAllocations: components,
    allocationLimitPolicy,
    profitSharingConditions: {
      minHours: isFiniteNumber(conditions.minHours) ? Math.max(0, Math.round(conditions.minHours)) : 0,
      lastDayRequired: conditions.lastDayRequired === true,
    },
  }
}

// Whole-plan compilation: authored plan → ephemeral calculation shape.
export function compileToEngine(plan) {
  if (!isAuthoredPlan(plan)) {
    throw new Error('compileToEngine requires the current authored plan contract.')
  }
  const contributions = plan.employerContributions || {}
  const { profitSharing: _design, ...restEmployerContributions } = contributions
  const cashBalance = selectCashBalance(plan)
  return {
    ...plan,
    employerContributions: normalizeEngineEmployerContributions({
      ...restEmployerContributions,
      ...compileProfitSharingToEngine(contributions.profitSharing),
    }),
    // Class labels are resolved at runtime from the census (calculations.js
    // supplies groupLabelsById), so compilation stays plan-only and passes
    // through whatever label the credit authored for itself.
    cashBalance: cashBalance
      ? {
          ...cashBalance,
          groups: (cashBalance.groups || []).map((group) => ({
            ...group,
            groupLabel: group.groupLabel || '',
          })),
        }
      : null,
  }
}

// =============================================================================
// Runtime group resolution (§2.5a). Pure; called by calculatePlanResults with
// classified rows. Never writes census state.
// =============================================================================

// Membership is the census row's class code, full stop. There is no rule
// engine, no precedence order and no local sidecar: a person's class is an
// input the author typed, so it cannot move when their compensation or the
// plan year changes.
export function resolveGroupMembership(employees = []) {
  return resolveClassMembership(employees)
}

// Display/projection lookup: class id → label (blank for the catch-all).
export function groupLabelById(allocationClasses = [], groupId) {
  return classLabelById(allocationClasses, groupId)
}

export function resolveSolverTargets(employees = [], targets, slotLocal = null, source = 'profitSharing') {
  if (targets?.rule === 'all-owners') {
    return employees.filter((employee) => employee.isFivePercentOwner === true).map((employee) => employee.id)
  }
  if (targets?.local === true) {
    const ids = slotLocal?.solverTargets?.[source]
    return Array.isArray(ids) ? ids.filter((id) => employees.some((employee) => employee.id === id)) : []
  }
  return []
}

// =============================================================================
// Group review findings and CSV projection (§2.2, §4 Phase 3). Pure selectors
// consumed by the Groups stage, the Results review rows, and CSV export.
// =============================================================================

// Typed review findings for one plan: classes a formula points at that nobody
// in the census carries, plus the engine's zero-with-review lists (missing
// class formulas, missing per-person amounts). Never mutates anything.
export function collectGroupReviewFindings({ allocationClasses = [], allocationDiagnostics = [] } = {}) {
  const findings = []
  const orphans = (allocationClasses || []).filter((entry) => entry.source === 'plan')
  if (orphans.length) {
    findings.push({
      code: 'class-without-members',
      message: `${orphans.length === 1 ? 'A class has' : `${orphans.length} classes have`} a formula but nobody in the census: ${orphans.map((entry) => entry.label).join(', ')}. Assign employees on the Census step or remove the rate.`,
    })
  }
  for (const diagnostic of allocationDiagnostics) {
    if (diagnostic?.missingFormulaEmployeeIds?.length) {
      findings.push({
        code: 'missing-group-formula',
        employeeIds: diagnostic.missingFormulaEmployeeIds,
        message: 'Participants resolve to a group with no formula for this source; they receive zero until a formula is defined.',
      })
    }
    if (diagnostic?.missingAmountEmployeeIds?.length) {
      findings.push({
        code: 'missing-per-person-amount',
        employeeIds: diagnostic.missingAmountEmployeeIds,
        message: 'This per-person design has no local amount for these participants; they receive zero until amounts are entered on this device.',
      })
    }
  }
  return findings
}

// =============================================================================
// censusLocal sidecar shapes (§2.2)
// =============================================================================

// Class membership is NOT here: it lives on the census row's allocation_group,
// which is the user's own file and never uploaded. What remains browser-only is
// data that has no census column — solver target picks and per-person amounts.
export const createEmptySlotLocal = () => ({
  solverTargets: { profitSharing: [] },
  perPersonAmounts: { profitSharing: {} },
})

export function normalizeSlotLocal(value) {
  const empty = createEmptySlotLocal()
  if (!isObject(value)) return empty
  const cleanAmounts = (map) => (isObject(map)
    ? Object.fromEntries(Object.entries(map).filter(([, v]) => isFiniteNumber(v) && v >= 0))
    : {})
  return {
    solverTargets: {
      profitSharing: Array.isArray(value.solverTargets?.profitSharing)
        ? value.solverTargets.profitSharing.filter((id) => typeof id === 'string' && id) : [],
    },
    perPersonAmounts: {
      profitSharing: cleanAmounts(value.perPersonAmounts?.profitSharing),
    },
  }
}

export function normalizeCensusLocal(value) {
  const source = isObject(value) ? value : {}
  const byPlanKey = isObject(source.byPlanKey) ? source.byPlanKey : {}
  return {
    censusInstanceId: typeof source.censusInstanceId === 'string' && source.censusInstanceId
      ? source.censusInstanceId
      : null,
    byPlanKey: Object.fromEntries(
      ['planA', 'planB', 'planC', 'planD']
        .filter((key) => isObject(byPlanKey[key]))
        .map((key) => [key, normalizeSlotLocal(byPlanKey[key])]),
    ),
  }
}

// Remove a deleted employee from every map and target list of one slot entry.
export function pruneEmployeeFromSlotLocal(slotLocal, employeeId) {
  const next = normalizeSlotLocal(slotLocal)
  next.solverTargets.profitSharing = next.solverTargets.profitSharing.filter((id) => id !== employeeId)
  delete next.perPersonAmounts.profitSharing[employeeId]
  return next
}
